import type { ChangeEvent, ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TypeDot } from '../../shared/pills'
import type { LibraryItem } from '../_lib/filter-engine'
import { CorpusStatus } from '../shared/corpus-status'

export interface LinkQuestionModalProps {
  open: boolean
  query: string
  items: LibraryItem[]
  onQueryChange: (query: string) => void
  onOpenChange: (open: boolean) => void
  onLink: (item: LibraryItem) => void
  persisted?: boolean
  /**
   * The `items` corpus stopped draining short of the whole library, so this
   * dialog's search answers for a prefix of it. Passed in rather than read
   * from the store: this stays a presentational component.
   */
  incomplete?: boolean
  /**
   * The corpus is still filling. Worth saying here and not only in the table
   * because `PersistedCurriculumBoundary` unblocks the tab as soon as page ONE
   * lands: this dialog is openable and searchable over ~100 of ~670 items,
   * with the same failure mode as the table — no hit reads as "no such
   * question".
   */
  streaming?: boolean
}

export function LinkQuestionModal(props: LinkQuestionModalProps): ReactNode {
  const {
    open,
    query,
    items,
    onQueryChange,
    onOpenChange,
    onLink,
    persisted = false,
    incomplete = false,
    streaming = false,
  } = props
  const normalizedQuery = query.trim().toLowerCase()
  const candidates = persisted
    ? items.filter(
        (item) =>
          item.type === 'coding' ||
          item.type === 'sd' ||
          item.type === 'behavioral',
      )
    : items
  const filteredItems = normalizedQuery
    ? candidates.filter((item) =>
        [item.title, item.topic, item.subtopic].some((value) =>
          value.toLowerCase().includes(normalizedQuery),
        ),
      )
    : candidates

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onQueryChange(event.target.value)
  }

  const handleLink = (item: LibraryItem): void => {
    onLink(item)
    onOpenChange(false)
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="coaching-dashboard gap-0 border-(--cd-border-4) bg-(--cd-panel) px-[18px] py-4 text-(--cd-text) shadow-[0_24px_60px_rgba(0,0,0,.6)] sm:max-w-[580px]">
        <DialogHeader className="mb-2.5 flex-row items-center gap-2.5 pr-8 text-left">
          <DialogTitle className="text-[12.5px] leading-normal font-bold">
            🤖 Link a question to this AI mock
          </DialogTitle>
          <DialogDescription className="text-[10.5px] text-(--cd-text-faint)">
            {persisted ? 'The client' : 'Jordan'} will only ever see &quot;AI
            mock interview&quot;
          </DialogDescription>
        </DialogHeader>

        {/* Same corpus as the library table, so the same warning: a search
            that misses here must not read as "no such question". Placed above
            the input it qualifies — the dialog mounts this region already
            populated, so `announceOnMount` swaps aria-live for role="alert",
            which is announced on insertion. */}
        <CorpusStatus
          announceOnMount
          className="mb-2"
          incomplete={incomplete}
          label="Question list loading status"
          streaming={streaming}
        />

        <input
          aria-label="Search the library"
          className="mb-2.5 w-full rounded-lg border border-(--cd-border-2) bg-(--cd-bg) px-[11px] py-2 text-xs text-(--cd-text) outline-none placeholder:text-(--cd-text-faint) focus:border-(--cd-border-4)"
          onChange={handleQueryChange}
          placeholder="Search the library…"
          type="search"
          value={query}
        />

        <div className="flex max-h-[300px] flex-col gap-1 overflow-y-auto">
          {filteredItems.map((item) => (
            <div
              className="flex items-center gap-[9px] rounded-lg border border-(--cd-border) bg-(--cd-card) px-[11px] py-[7px]"
              key={item.id}
            >
              <TypeDot type={item.type} />
              <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold">
                {item.title}
              </span>
              <span className="whitespace-nowrap text-[10px] text-(--cd-text-muted)">
                {item.topic} / {item.subtopic} · {item.difficulty} · ELO{' '}
                {item.elo ?? '—'}
              </span>
              <button
                aria-label={`Link ${item.title}`}
                className="cursor-pointer whitespace-nowrap rounded-md border-0 bg-(--cd-green) px-[11px] py-0.5 text-[10px] font-bold text-(--cd-accent-contrast) hover:bg-(--cd-green-hover)"
                onClick={() => handleLink(item)}
                type="button"
              >
                Link
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
